import {
  computed,
  inject,
  Injectable,
  resource,
  ResourceLoaderParams,
  ResourceRef,
  signal,
  Signal,
  WritableSignal,
} from '@angular/core';
import { CONFIGURATION_SETTING_KEYS, ITariffRelationLocation } from '@web/dtos';
import { IApiResponse } from '@web/shared-types';
import { firstValueFrom } from 'rxjs';
import { IOriginRequest, OriginsGateway } from './origins.gateway';
import { ConfigurationStore } from '../../configuration/configuration-store.service';

@Injectable({
  providedIn: 'root',
})
export class OriginsStore {
  private readonly configurationStore: ConfigurationStore =
    inject(ConfigurationStore);
  private readonly isRelationSearchLoadAllStopsActive = computed(() =>
    this.configurationStore.getBooleanConfiguration(
      CONFIGURATION_SETTING_KEYS.IsRelationSearchLoadAllStopsActive
    )
  );
  private readonly serviceAreaId = computed(() =>
    this.configurationStore.getNumberConfiguration(
      CONFIGURATION_SETTING_KEYS.ServiceAreaId
    )
  );
  private readonly relationSearchStopLimit = computed(() =>
    this.configurationStore.getNumberConfiguration(
      CONFIGURATION_SETTING_KEYS.RelationSearchStopLimit
    )
  );

  private readonly gateway: OriginsGateway = new OriginsGateway();

  private readonly request: Signal<IOriginRequest> = computed(() => {
    return {
      loadAllStops: this.isRelationSearchLoadAllStopsActive(),
      serviceAreaId: this.serviceAreaId(),
      maxResults: this.relationSearchStopLimit(),
      name: this.originName(),
    };
  });
  private readonly originName: WritableSignal<string> = signal('');

  #originsResource: ResourceRef<
    IApiResponse<ITariffRelationLocation[]> | undefined
  > = resource({
    params: this.request,
    loader: ({
      params,
    }: ResourceLoaderParams<IOriginRequest>): Promise<
      IApiResponse<ITariffRelationLocation[]>
    > => {
      return firstValueFrom(
        this.gateway.get({
          loadAllStops: params.loadAllStops,
          serviceAreaId: params.serviceAreaId,
          maxResults: params.maxResults,
          name: params.name,
        })
      );
    },
  });

  readonly isLoading: Signal<boolean> = computed((): boolean =>
    this.#originsResource.isLoading()
  );
  readonly origins: Signal<ITariffRelationLocation[]> = computed(
    (): ITariffRelationLocation[] => this.#originsResource.value()?.data ?? []
  );

  reload(): void {
    this.#originsResource.reload();
  }

  public loadOrigins(name: string): void {
    this.originName.set(name);
  }
}
